import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Linkedin } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t('nav.home'), id: 'hero' },
    { label: t('nav.about'), id: 'about' },
    { label: t('nav.products'), id: 'catalog' },
    { label: t('nav.applications'), id: 'applications' },
    { label: t('nav.contact'), id: 'contact' }
  ];

  const productLinks = [
    t('footer.productLinks.treatedWood'),
    t('footer.productLinks.metalPosts'),
    t('footer.productLinks.structuralWood'),
    t('footer.productLinks.decking'),
    t('footer.productLinks.accessories')
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_navarra-supply/artifacts/qn3t6vcs_logo_cmv-removebg-preview.png"
              alt="CMV"
              className="h-12 md:h-16 w-auto mb-4"
            />
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t('footer.about.description')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/cmv_maderas?igsh=aHU1NGM3dHpxOXFt"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cmv-green transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/cmv-maderas-tratadas-sl/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-cmv-green transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-cmv-green transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.products')}</h3>
            <ul className="space-y-3">
              {productLinks.map((product) => (
                <li key={product}>
                  <button
                    onClick={() => scrollToSection('catalog')}
                    className="text-gray-400 hover:text-cmv-green transition-colors"
                  >
                    {product}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={20} className="text-cmv-green flex-shrink-0 mt-1" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-white transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={20} className="text-cmv-green flex-shrink-0 mt-1" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-white transition-colors">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-cmv-green flex-shrink-0 mt-1" />
                <span className="text-gray-400">
                  {contactInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>
              © {currentYear} CMV. {t('footer.copyright')}
            </p>
            
            {/* Logos oficiales */}
            <div className="flex justify-center items-center my-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_navarra-supply/artifacts/sygt0zs6_logo%20financiado%20por.jpg"
                alt="Financiado por la Unión Europea - NextGenerationEU, Ministerio para la Transición Ecológica, IDAE, Plan de Recuperación y Gobierno de Navarra"
                className="h-24 md:h-32 w-auto object-contain"
              />
            </div>
            
            <div className="flex gap-6">
              <Link 
                to="/politica-privacidad" 
                className="hover:text-white transition-colors"
              >
                {t('footer.privacy')}
              </Link>
              <Link 
                to="/aviso-legal" 
                className="hover:text-white transition-colors"
              >
                {t('footer.legal')}
              </Link>
              <Link 
                to="/politica-cookies" 
                className="hover:text-white transition-colors"
              >
                {t('footer.cookies')}
              </Link>
            </div>
          </div>
          
          {/* Logo LOPD y RGPD - Certificado de Adecuación */}
          <div className="mt-8 pt-6 border-t border-gray-800 flex justify-center">
            <a 
              href="https://customer-assets.emergentagent.com/job_navarra-supply/artifacts/hptwv4ik_3%20-%20CMV-MADERAS-TRATADAS-S.L.-CARTELES-Certificado-ADECUACION.pdf"
              download="CMV-Certificado-Adecuacion-LOPD-RGPD.pdf"
              className="transition-transform hover:scale-105"
              aria-label="Descargar Certificado de Adecuación LOPD y RGPD"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_navarra-supply/artifacts/snmt6bar_image.png"
                alt="Empresa Adaptada - Ley Orgánica LOPD y RGPD - Prodana Consultores"
                className="h-20 md:h-24 w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
